// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: f56f668d-0f93-4dbe-9a5f-1e4d1f9909ea

// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert, Typography,
  Box, Stack, Link,
} from '@mui/material';
import {
  getSession, mfaStatus, beginAuthenticatorEnrollment, completeAuthenticatorEnrollment,
  disableAuthenticator,
} from './auth';
import { QrCode, safeQrEncode } from './Login';
import HelpBubble from './HelpBubble';

// Self-service authenticator (TOTP) management for whoever is signed in —
// the sibling of ChangePassword, and deliberately shaped like it. This screen
// exists to PREVENT lockouts: verifying a new secret REPLACES the old
// association, and it needs a live session, so the moment to use it is while
// you still have one. There is no admin-side counterpart on purpose — the only
// admin "reset" Cognito offers flips a preference while the old token
// association survives, which is a lockout, not a reset.

function errorMessage(e) {
  switch ((e && e.name) || '') {
    case 'CodeMismatchException':
    case 'EnableSoftwareTokenMFAException':
      return 'That code is not correct. Check that you are reading the entry you JUST added, '
        + 'and enter the six digits it shows right now.';
    case 'ExpiredCodeException':
      return 'That code has already expired. Authenticator codes change every 30 seconds — '
        + 'wait for the next one and enter it straight away.';
    case 'NotAuthorizedException':
      // A dead access token (8-hour life). The only way forward is a fresh
      // sign-in — say so instead of showing a raw SDK error.
      return 'Your session has expired. Sign out, sign back in, and try again.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Too many attempts. Wait a few minutes before trying again.';
    default:
      return (e && e.message) || 'Something went wrong. Please try again.';
  }
}

// `required` turns this from a settings screen into a gate: an account that can
// read across every client is not allowed to sit behind a password alone. The
// dialog then has no escape — no backdrop dismiss, no Escape, no Cancel — and
// closes only when Cognito reports the factor is actually enrolled. It is NOT
// security on its own (a determined caller can hit the API with a valid token),
// but it is what makes "everyone with cross-client access has a second factor"
// true in practice rather than aspirational.
export default function AuthenticatorSettings({ open, onClose, required = false, onSignOut }) {
  // null = loading, otherwise { enrolled: true|false|null, reason? }
  const [status, setStatus] = useState(null);
  // { secret, otpauth } while an enrollment is in flight.
  const [enrollment, setEnrollment] = useState(null);
  const [code, setCode] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [confirmingDisable, setConfirmingDisable] = useState(false);
  const [done, setDone] = useState(null); // 'enrolled' | 'disabled'
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const session = getSession();
  // A federated user's MFA lives with their identity provider (Entra, Google,
  // …) — Cognito holds no authenticator for them, so every control on this
  // screen would be a control that can only fail. Info strip only.
  const federated = !!session && session.sso;

  useEffect(() => {
    if (!open || federated) return;
    setStatus(null);
    mfaStatus().then(setStatus);
  }, [open, federated]);

  const qrMatrix = useMemo(
    () => (enrollment && enrollment.otpauth ? safeQrEncode(enrollment.otpauth) : null),
    [enrollment],
  );

  const close = () => {
    // A required enrolment cannot be walked away from. The only way out is to
    // finish it, or sign out.
    if (required && !(status && status.enrolled)) return;
    // Drop the secret the moment the dialog goes away, whatever state it is in.
    setEnrollment(null); setCode(''); setShowSecret(false);
    setConfirmingDisable(false); setDone(null); setErr(''); setStatus(null);
    onClose();
  };

  const startEnrollment = async () => {
    setErr(''); setDone(null); setBusy(true);
    try {
      setEnrollment(await beginAuthenticatorEnrollment());
    } catch (e) {
      setErr(errorMessage(e));
    } finally { setBusy(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { setErr('Enter all six digits of the code.'); return; }
    setErr(''); setBusy(true);
    try {
      await completeAuthenticatorEnrollment(code);
      // Enrollment is done: forget the secret before anything else happens.
      setEnrollment(null); setCode(''); setShowSecret(false);
      setDone('enrolled');
      setStatus({ enrolled: true });
    } catch (e2) {
      setErr(errorMessage(e2));
      setCode('');
    } finally { setBusy(false); }
  };

  const doDisable = async () => {
    setErr(''); setBusy(true);
    try {
      await disableAuthenticator();
      setConfirmingDisable(false);
      setDone('disabled');
      setStatus({ enrolled: false });
    } catch (e) {
      setErr(errorMessage(e));
    } finally { setBusy(false); }
  };

  const enrolled = !!(status && status.enrolled);
  const secret = enrollment ? enrollment.secret : '';
  const grouped = secret.replace(/(.{4})/g, '$1 ').trim();

  // What the dialog shows, exactly one of these at a time.
  let body;
  if (federated) {
    body = (
      <Alert severity="info">
        You sign in through your organisation&apos;s own account, so two-step verification is
        managed by your organisation&apos;s identity provider, not by Primordia. Nothing here
        applies to your account.
      </Alert>
    );
  } else if (enrollment) {
    body = (
      <form onSubmit={submitCode} id="authenticator-verify-form">
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Typography sx={{ fontSize: 13, color: '#5C6B7A', mb: 2 }}>
          In your authenticator app (Microsoft Authenticator, Google Authenticator, 1Password,
          or similar), scan this code, then confirm with the six digits the app shows.
          {enrolled && ' Verifying the new entry replaces your old one — the old authenticator stops working the moment this succeeds.'}
        </Typography>

        {qrMatrix ? (
          <>
            <Stack alignItems="center" sx={{ mb: 2 }}>
              <Box sx={{ p: 1.25, bgcolor: '#fff', border: '1px solid', borderColor: 'divider', borderRadius: 1, lineHeight: 0 }}>
                <QrCode matrix={qrMatrix} />
              </Box>
            </Stack>
            <Typography sx={{ fontSize: 12, color: '#5C6B7A', mb: 1 }}>
              Cannot scan it?
              {' '}
              <Link component="button" type="button" underline="hover" onClick={() => setShowSecret(true)}
                sx={{ fontSize: 12, color: '#0D5B9A', fontWeight: 700, verticalAlign: 'baseline' }}>
                Enter the key by hand
              </Link>
              .
            </Typography>
          </>
        ) : (
          <Alert severity="info" sx={{ mb: 2, fontSize: 12.5 }}>
            No QR code is shown here — this browser could not draw one. Add the account by hand
            using the setup key below; it works exactly the same.
          </Alert>
        )}

        {(showSecret || !qrMatrix) && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#5C6B7A', letterSpacing: 0.8, mb: 0.75 }}>
              SETUP KEY
            </Typography>
            <Typography component="div" sx={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              fontSize: 15, fontWeight: 700, color: 'sectionHeader.main', letterSpacing: 1.2,
              wordBreak: 'break-all', userSelect: 'all', mb: 0.75,
            }}>
              {grouped}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: '#5C6B7A' }}>
              In your authenticator app choose &ldquo;add account&rdquo; then &ldquo;enter a setup
              key manually&rdquo;, name it Primordia, and type the key above (spaces do not
              matter). This key is shown only during setup and never again.
            </Typography>
          </Box>
        )}

        <TextField fullWidth label="6-digit code" value={code} size="small" required
          onChange={(e2) => setCode(e2.target.value.replace(/\D/g, '').slice(0, 6))}
          inputProps={{
            inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6, autoComplete: 'one-time-code',
            style: { letterSpacing: 6, fontSize: 20, fontWeight: 700, textAlign: 'center', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
          }}
          helperText="Codes change every 30 seconds." />
      </form>
    );
  } else if (confirmingDisable) {
    body = (
      <>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 0.5 }}>Turn off two-step verification?</Typography>
          Primordia will stop asking for an authenticator code when you sign in — your password
          alone will sign you in, which is less safe. This does <b>not</b> remove the Primordia
          entry from your authenticator app (nothing can); it simply stops being asked for. If
          you set it up again later, the new entry replaces the old one.
        </Alert>
      </>
    );
  } else if (done === 'enrolled') {
    body = (
      <Alert severity="success">
        Your authenticator is set up. From your next sign-in, Primordia will ask for the
        six-digit code it shows. If this replaced an old authenticator, the old one no longer
        works — you can delete its Primordia entry.
      </Alert>
    );
  } else if (done === 'disabled') {
    body = (
      <Alert severity="success">
        Two-step verification is off. Your password alone now signs you in. The Primordia entry
        left in your authenticator app is inert — you can delete it, and you can turn two-step
        verification back on here any time.
      </Alert>
    );
  } else if (status === null) {
    body = <Typography sx={{ fontSize: 13, color: '#5C6B7A' }}>Checking your authenticator status…</Typography>;
  } else if (status.enrolled === null) {
    body = <Alert severity="warning">{status.reason}</Alert>;
  } else {
    body = (
      <>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Typography sx={{ fontSize: 13, color: '#5C6B7A', mb: 1.5 }}>
          Signed in as
          {' '}
          <b>{session ? session.email : ''}</b>
          .
        </Typography>
        {enrolled ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            An authenticator app is set up on this account. Sign-in asks for its six-digit code.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            No authenticator app is set up on this account.
          </Alert>
        )}
        <Typography sx={{ fontSize: 12.5, color: '#5C6B7A' }}>
          {enrolled
            ? 'Getting a new phone, or lost the old authenticator but still signed in here? '
              + 'Replace it now, while you have this session — setting up a new authenticator '
              + 'replaces the old one in the same step. There is no reset that anyone, '
              + 'including Optimum, can do for you afterwards.'
            : 'An authenticator app adds a six-digit code to your sign-in, so your password '
              + 'alone is not enough to get in.'}
        </Typography>
      </>
    );
  }

  const statusView = !federated && !enrollment && !confirmingDisable && !done
    && !!status && status.enrolled !== null;

  return (
    <Dialog
      open={open}
      onClose={() => { if (!busy && !required) close(); }}
      disableEscapeKeyDown={required}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>Authenticator app <HelpBubble topic="authenticatorApp" /></DialogTitle>
      <DialogContent dividers>{body}</DialogContent>
      <DialogActions>
        {enrollment && (
          <Button onClick={() => { setEnrollment(null); setCode(''); setShowSecret(false); setErr(''); }}
            disabled={busy}>
            Back
          </Button>
        )}
        {confirmingDisable && (
          <Button onClick={() => { setConfirmingDisable(false); setErr(''); }} disabled={busy}>
            Keep it on
          </Button>
        )}
        {required && !(status && status.enrolled) ? (
          <Button onClick={onSignOut} disabled={busy} color="inherit">Sign out instead</Button>
        ) : (
          <Button onClick={close} disabled={busy}>{statusView || enrollment ? 'Cancel' : 'Close'}</Button>
        )}
        {statusView && enrolled && (
          <Button onClick={() => { setErr(''); setConfirmingDisable(true); }} disabled={busy}
            color="error">
            Turn off
          </Button>
        )}
        {statusView && (
          <Button variant="contained" onClick={startEnrollment} disabled={busy}
            sx={{ bgcolor: '#0D5B9A', '&:hover': { bgcolor: '#021F4B' } }}>
            {busy ? 'Starting…' : (enrolled ? 'Replace authenticator' : 'Set up authenticator')}
          </Button>
        )}
        {enrollment && (
          <Button type="submit" form="authenticator-verify-form" variant="contained"
            disabled={busy || code.length !== 6}
            sx={{ bgcolor: '#0D5B9A', '&:hover': { bgcolor: '#021F4B' } }}>
            {busy ? 'Verifying…' : 'Verify & finish'}
          </Button>
        )}
        {confirmingDisable && (
          <Button variant="contained" color="error" onClick={doDisable} disabled={busy}>
            {busy ? 'Turning off…' : 'Turn off two-step verification'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
